import { ChangeDetectorRef, Component, inject, Input, PLATFORM_ID, SimpleChanges, ViewChild, OnInit, OnChanges } from '@angular/core';
import { forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Property, UserProperty, PropertyViewModel } from '../../../../../features/property/models/property.model';
import { User } from '../../../models/user.model';
import { PropertyService } from '../../../../../features/property/services/property.service';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterLink } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DialogService } from '../../../../services/dialog.service';
import { MatDialogModule } from '@angular/material/dialog';
import { ManagePropertyCalendar } from '../manage-property-calendar/manage-property-calendar';
import { ManagePropertyPricing } from '../manage-property-pricing/manage-property-pricing';
import { SheetService } from '../../../../services/sheet.service';
import { MatCardModule } from '@angular/material/card';
import { QrGenerator } from '../../../../general/components/qr-generator/qr-generator';
import { ImageGeneratorComponent } from '../../../../general/components/image-generator/image-generator';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { PaymentService } from '../../../../general/services/payment.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HapticsService } from '../../../../general/services/haptics.service';
import { PropertyLikeService } from '../../../../../features/property/services/property-like.service';
import { SeoService } from '../../../../services/seo.service';
import { GtmService } from '../../../../../core/services/gtm.service';
import { environment } from '../../../../../../environments/environment';
import { AuthService } from '../../../../../core/services/auth.service';


@Component({
  selector: 'app-user-property-list',
  imports: [
    CommonModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDialogModule,
    MatCardModule,
    RouterLink,
    MatDividerModule,
    MatMenuModule
  ],
  templateUrl: './user-property-list.html',
  styleUrl: './user-property-list.scss',
})
export class UserPropertyList implements OnInit, OnChanges {
  private propertyService = inject(PropertyService);
  private likeService = inject(PropertyLikeService);
  private dialogService = inject(DialogService);
  private paymentService = inject(PaymentService);
  private sheetService = inject(SheetService);
  private snackBar = inject(MatSnackBar);
  private router = inject(Router);
  private haptics = inject(HapticsService);
  private cdr = inject(ChangeDetectorRef);

  private seo = inject(SeoService);
  private gtm = inject(GtmService);
  private auth = inject(AuthService);
  platformId = inject(PLATFORM_ID);
  isLoading = false;
  noPropertiesFound = false;
  @Input() user!: User;
  userProperties: UserProperty[] = [];
  properties: Property[] = [];
  displayedColumns: string[] = ['isPublished', 'image', 'displayName', 'location', 'zipCode', 'saveCount', 'actions'];
  readonly SKELETON_DATA = Array.from({ length: 5 }, () => ({} as PropertyViewModel));
  dataSource = new MatTableDataSource<PropertyViewModel>(this.SKELETON_DATA);
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      document.body.scrollTop = 0;
      if (this.user) {
        this.loadUserProperties();
      }
      this.gtm.pageView({
        page_path: '/profile/manage-properties',
        page_title: 'Manage Properties | Horizinns'
      });
    }

    this.seo.updateMetaTags(
      'Manage Properties',
      'Manage user\'s hosted properties on Horizinns',
      undefined,
      `${environment.appUrl}/profile/manage-properties`
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['user'] && changes['user'].currentValue) {
      this.user = changes['user'].currentValue;
      this.loadUserProperties();
    }
  }

  createProperty() {
    this.haptics.click();
    this.clearDraft();
    this.router.navigate(['/property/create']);
  }

  loadUserProperties = () => {
    this.isLoading = true;

    this.propertyService.getUserProperties(this.user.id).pipe(
      switchMap((userProperties: UserProperty[]) => {
        if (!userProperties?.length) {
          this.noPropertiesFound = true;
          return of([]);
        }

        const propertyRequests = userProperties.map(up =>
          this.propertyService.getPropertyById(up.propertyId).pipe(
            switchMap(prop =>
              forkJoin({
                saveCount: this.likeService.getPropertyLikeCount(prop.id),
                images: this.propertyService.getPropertyImages(prop.id)
              }).pipe(
                map(({ saveCount, images }) => {
                  const primary = images?.find(img => img.sortOrder === 1) ?? images?.[0];
                  const imageUrl = primary ? primary.imageUrl : 'assets/placeholder.jpg';

                  return { ...prop, saveCount, primaryImageUrl: imageUrl } as PropertyViewModel;
                })
              )
            )
          )
        );

        return forkJoin(propertyRequests);
      })
    ).subscribe({
      next: (properties: PropertyViewModel[]) => {
        this.setupDataSource(properties);
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading properties:', err);
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private getDraftKey() {
    return `property-draft-${this.auth.userSubject.value?.id}`;
  }

  private clearDraft() {
    const key = this.getDraftKey();
    localStorage.removeItem(key);
    console.log('✅ Payment successful — cleared draft');
  }

  private setupDataSource(properties: PropertyViewModel[]): void {
    this.properties = properties;
    this.dataSource.data = properties;

    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
    }
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
  }

  getPropertyPrimaryImageUrl(propertyId: string): Observable<string> {
    console.log('get image', propertyId);
    return this.propertyService.getPropertyImages(propertyId).pipe(
      map(images => {
        // Ensure images exists and has content
        if (!images || images.length === 0) {
          return 'assets/placeholder.jpg';
        }

        const primary = images.find(img => img.sortOrder === 1) ?? images[0];
        return primary.imageUrl;
      })
    );
  }

  getPropertySaveCount(propertyId: string): Observable<number> {
    return this.likeService.getPropertyLikeCount(propertyId);
  }

  editPricing(property: Property) {
    this.haptics.click();
    console.log(`edit pricing ${property.id}`);
    const ref = this.dialogService.open(ManagePropertyPricing, {
      data: property,
      width: '800px'
    });
    ref.afterClosed().subscribe(result => {
      if (result) {
        console.log('pricing result', result);
      }
    });
  }

  manageBooking(property: Property) {
    this.haptics.click();
    console.log(`manage property ${property.id}`);
    const ref = this.dialogService.open(ManagePropertyCalendar, {
      data: property,
      width: '800px'
    });

    ref.afterClosed().subscribe(result => {
      if (result) {
        console.log('booking result', result);
      }
    });
  }

  openQrCode(propertyId: string) {
    this.haptics.click();
    this.dialogService.open(QrGenerator, {
      data: { propertyId }
    });
  }

  viewProperty(property: Property) {
    this.haptics.click();
    this.router.navigate(['/property', property.id]);
  }

  openImageGenerator(property: Property) {
    this.haptics.click();
    console.log('property image generator', property.id, property.images);
    this.dialogService.open(ImageGeneratorComponent, {
      data: { property: property },
      panelClass: 'image-generator-dialog'
    });
  }

  publishProperty(propertyId: string) {
    this.haptics.click();
    this.router.navigate(['/payment', propertyId]);
  }

  cancelSubscription(propertyId: string) {
    this.haptics.click();
    const ref = this.dialogService.confirm(
      'Are you sure you want to cancel this property listing at the conclusion of the current term?',
      'Cancel Listing',
      'Yes, Cancel Listing',
      'Nevermind'
    );

    ref.subscribe(result => {
      if (result === true) {
        this.paymentService.cancelSubscriptionByProperty(propertyId).subscribe({
          next: (response: string) => {
            this.snackBar.open(response, 'OK', {
              duration: 8000,
              panelClass: ['success-snackbar']
            });
          },
          error: (err) => {
            let msg = 'Failed to cancel subscription.';
            if (err.status === 401) msg = 'Please log in again.';
            if (err.status === 403) msg = 'You do not have permission.';
            if (err.status === 404) msg = 'No active subscription found.';
            if (err.status === 409) msg = 'Already canceled or scheduled.';
            if (err.error) msg = err.error;

            this.snackBar.open(msg, 'Close', {
              duration: 10000,
              panelClass: ['error-snackbar']
            });
          }
        });
      }
    });
  }
}
